/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-this-alias */
import L from "leaflet";
import { select, create } from "d3-selection";
import { arc } from "d3-shape";
import $ from "jquery";
import { svgIconExtends } from "./SvgIcon";
import configurationUtils from "@/utils/IconConfiguration";

interface ClusterIconOptions extends L.DivIconOptions {
  id?: string | number;
  width?: number;
  count: number;
  alertSeverity?: string | number | null;
  motion?: number | null;
  state?: string;
  svgString?: string;
  bgPos?: [number, number];
}

interface IconInfo {
  bgColor?: string;
  iconColor: string;
  bgHoverColor?: string;
  iconHoverColor: string;
}

// Extend Leaflet types
declare module "leaflet" {
  class ClusterIcon {
    options: ClusterIconOptions;
    constructor(options?: ClusterIconOptions);
    createIcon(): HTMLElement;
    setStyle(el: Element, style: string): void;
    onEnter(): void;
    onLeave(): void;
    onClick(): void;
    beforeInject(img: Element, svg: Element): void;
  }

  function clusterIcon(opts: ClusterIconOptions): ClusterIcon;
}

(L as any).ClusterIcon = svgIconExtends.extend({
  options: {
    className: "cluster-svg-icon",
    id: "",
    width: 30,
    count: 123,
    alertSeverity: null,
    motion: null,
  },

  createIcon: function (): HTMLElement {
    const div = document.createElement("div");
    const options = this.options as ClusterIconOptions;

    div.id = "svg-icon-" + options.id;
    (div as any).name = options.state;

    if (options.svgString) {
      this._createSvg(options.svgString, div);
    } else {
      const width = options.width || 30;
      // Create SVG string using D3
      const svg = create("svg")
        .attr("width", width)
        .attr("height", width)
        .attr("viewBox", `0 0 ${width} ${width}`)
        .attr("shape-rendering", "geometricPrecision");

      const center = width / 2;
      const radius = width / 2 - 1;
      const innerRadius = width / 2 - 3;
      const centerRadius = width / 2 - 5;
      // Scale the count with the icon, but never let it overflow the center disc
      const digits = options.count.toString().length;
      const maxFontSize = Math.floor((centerRadius * 2 - 4) / (0.6 * digits));
      const fontSize = Math.min(
        Math.max(11, Math.round(width * 0.3)),
        Math.max(9, maxFontSize)
      );

      // Main circle group
      const mainGroup = svg
        .append("g")
        .attr("transform", `translate(${center},${center})`);

      // White backing disc: the gap between the outer ring and the center
      // circle is otherwise transparent and the map bleeds through.
      mainGroup
        .append("circle")
        .attr("id", "cluster-backing")
        .attr("r", radius)
        .attr("fill", "#FFFFFF");

      // Create arc generators
      const backgroundArc = arc()
        .innerRadius(innerRadius)
        .outerRadius(radius)
        .startAngle(0)
        .endAngle(2 * Math.PI)
        .cornerRadius(2);

      const progressArc = arc()
        .innerRadius(innerRadius)
        .outerRadius(radius)
        .startAngle(0)
        .endAngle(((options.motion || 0) / 100) * 2 * Math.PI)
        .cornerRadius(2);

      // Draw background circle
      mainGroup
        .append("path")
        .attr("d", backgroundArc as any)
        .attr("fill", "#304269")
        .style("stroke", "#304269")
        .style("stroke-width", "0.5px");

      // Draw progress arc if motion is set
      if (options.motion !== 0 && options.motion !== null) {
        mainGroup
          .append("path")
          .attr("d", progressArc as any)
          .attr("fill", "#39CB99")
          .style("stroke", "#39CB99")
          .style("stroke-width", "0.5px");
      }

      // Add center number with background circle
      const textGroup = mainGroup.append("g");

      textGroup
        .append("circle")
        .attr("id", "cluster-bg")
        .attr("r", centerRadius)
        .attr("fill", "#304269");

      textGroup
        .append("text")
        .attr("id", "cluster-text")
        .style("text-anchor", "middle")
        .style("dominant-baseline", "central")
        .style("font-size", `${fontSize}px`)
        .style("font-weight", "bold")
        .style("font-family", "Arial, sans-serif")
        .text(options.count.toString());

      // Add bell if alert is true
      if (options.alertSeverity !== null && options.alertSeverity !== undefined) {
        const bellGroup = mainGroup
          .append("g")
          .attr(
            "transform",
            `translate(${-radius * 1.5}, ${-radius * 2.2}) scale(${
              (width / 36) * 1.5
            })`
          );

        const alertbgColor = configurationUtils.getAlertColor(
          options.alertSeverity
        );
        bellGroup
          .append("path")
          .attr(
            "d",
            "M10.6 12.8C10.6 12.36 10.3 12 10 12C9.7 12 9.4 12.36 9.4 12.8V13.664C7.928 14.048 6.2 16.024 6.2 18.4V21.2L5.4 22H14.6L13.8 21.2V18.4C13.8 16.024 12.072 14.048 10.6 13.664V12.8ZM10 23.6C10.8 23.6 11.2 23 11.2 22.4H8.8C8.8 23 9.2 23.6 10 23.6Z"
          )
          .attr("fill", alertbgColor);
      }

      // Hand the d3-built node over as-is instead of serialising it to HTML
      const svgNode = svg.node();
      if (svgNode) {
        this._createSvg(svgNode, div);
      }
    }

    if (options.bgPos) {
      const bgPos = L.point(options.bgPos);
      div.style.backgroundPosition = -bgPos.x + "px " + -bgPos.y + "px";
    }
    this._setIconStyles(div, "icon");
    return div;
  },

  setStyle: function (el: Element, style: string): void {
    const options = this.options as ClusterIconOptions;
    let iconInfo: IconInfo | undefined;
    
    if (options.className === "cluster-svg-infras") {
      iconInfo = configurationUtils.getIcon("cluster_infras");
    } else if (options.className === "cluster-svg-geozone") {
      iconInfo = configurationUtils.getIcon("cluster_geozone");
    } else if (options.className === "cluster-svg-sitemap") {
      iconInfo = configurationUtils.getIcon("cluster_sitemap");
    } else {
      iconInfo = configurationUtils.getIcon("cluster");
    }

    if (!iconInfo) return;

    switch (style) {
      case "normal":
        $(el).find('[id^="cluster-bg"]').attr("fill", iconInfo.bgColor || "#333");
        $(el).find('[id^="cluster-fg"]').attr("stroke", iconInfo.iconColor);
        $(el).find('[id^="cluster-text"]').attr("fill", iconInfo.iconColor);
        break;
      case "hover":
        $(el).find('[id^="cluster-bg"]').attr("fill", iconInfo.bgHoverColor || "#333");
        $(el)
          .find('[id^="cluster-fg"]')
          .attr("stroke", iconInfo.iconHoverColor);
        $(el)
          .find('[id^="cluster-text"]')
          .attr("fill", iconInfo.iconHoverColor);
        break;
    }
  },

  onEnter: function (): void {},

  onLeave: function (): void {},

  onClick: function (): void {},

  beforeInject: function (img: Element, svg: Element): void {
    const self = this;
    const svg3 = select(svg);
    const width = self.options.width || 30;

    svg3.attr("width", width);
    svg3.attr("height", width);
    svg3.attr("viewBox", `0 0 ${width} ${width}`);

    svg3.select('[id^="cluster-bg"]').attr("cx", width / 2);
    svg3.select('[id^="cluster-bg"]').attr("cy", width / 2);
    svg3.select('[id^="cluster-bg"]').attr("r", width / 2 - 1);

    svg3.select('[id^="cluster-fg"]').attr("cx", width / 2);
    svg3.select('[id^="cluster-fg"]').attr("cy", width / 2);
    svg3.select('[id^="cluster-fg"]').attr("r", width / 2 - 3);

    svg3.select('[id^="cluster-text"]').text(self.options.count + "");
  },
}) as new (options?: ClusterIconOptions) => L.ClusterIcon;

L.clusterIcon = function (opts: ClusterIconOptions): L.ClusterIcon {
  let width = 40;
  if (opts.count >= 100 && opts.count < 1000) {
    width = 48;
  } else if (opts.count >= 1000) {
    width = 56;
  }
  return new L.ClusterIcon(
    Object.assign(opts, {
      width: width,
      id: opts.id,
      iconAnchor: [width / 2, width / 2] as [number, number],
    })
  );
};

export const ClusterIcon = L.clusterIcon;